// ============================================================
// VERIX - API Fetch utility with auto-refresh
// Central HTTP client for all API calls
// ============================================================

import type { ApiFetchOptions } from '../types';
import { SESSION_IDLE_TIMEOUT, handleIdleTimeout } from './idleTimeout';
import { rememberLoginReturn } from '../lib/loginReturn';

/** Id de la persona de la sesión que se cierra (para no devolver su URL a otra tras el login). */
function storedUserId(): string | null {
  try {
    return JSON.parse(localStorage.getItem('verix_user') || 'null')?.id ?? null;
  } catch {
    return null;
  }
}

export const API_BASE: string =
  (typeof window !== 'undefined' && (window as any).__VERIX_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  '';

// Candado en memoria: serializa el refresh DENTRO de esta pestaña. Entre
// pestañas lo serializa el Web Lock de `withRefreshLock`.
let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

// Dos pestañas comparten la cookie `verix_refresh` y cada una tiene su propio
// `isRefreshing`: sin un candado común, las dos canjean el mismo refresh a la
// vez y, con la rotación de Cognito encendida, la segunda es una reutilización.
const REFRESH_LOCK_NAME = 'verix-refresh';

// In-flight request deduplication â prevents duplicate GET requests
const inflightRequests = new Map<string, Promise<any>>();

// Invoke rate limiter â prevents auto-invoke storms (max N calls per window)
const INVOKE_RATE_LIMIT = 18; // raised from 10 â pages like Contabilidad need many panels
const INVOKE_RATE_WINDOW_MS = 60_000;
let _invokeTimestamps: number[] = [];
let _rateLimitWarned = false;

/** Check invoke rate limit — exported for SSE/streaming calls that bypass apiFetch */
export function checkInvokeRateLimit(): boolean {
  const now = Date.now();
  _invokeTimestamps = _invokeTimestamps.filter((ts) => now - ts < INVOKE_RATE_WINDOW_MS);
  if (_invokeTimestamps.length >= INVOKE_RATE_LIMIT) {
    // Only warn once per window to avoid console spam
    if (!_rateLimitWarned) {
      _rateLimitWarned = true;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- bottom-of-stack diagnostic; no captureLog to avoid recursion
        console.warn(
          `[apiFetch] invoke rate limit active: ${INVOKE_RATE_LIMIT} calls in ${INVOKE_RATE_WINDOW_MS / 1000}s â further requests dropped`
        );
      }
      setTimeout(() => { _rateLimitWarned = false; }, INVOKE_RATE_WINDOW_MS);
    }
    return false;
  }
  _invokeTimestamps.push(now);
  return true;
}

// Retry configuration for transient errors
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY = 500; // ms â doubles each attempt: 500, 1000, 2000
const RETRYABLE_STATUS_CODES = [502, 503, 504];

function processQueue(success: boolean): void {
  refreshQueue.forEach((cb) => cb(success ? 'ok' : null));
  refreshQueue = [];
}

/** Store CSRF token in sessionStorage (works cross-domain, unlike cookies) */
export function storeCsrfToken(token: string): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('verix_csrf', token);
  }
}

/** Sync CSRF cookie → sessionStorage after server-side rotation */
function syncCsrfFromCookie(): void {
  if (typeof document === 'undefined' || typeof sessionStorage === 'undefined') return;
  const match = document.cookie.match(/(?:^|;\s*)verix_csrf=([^;]*)/);
  if (match) {
    const cookieValue = decodeURIComponent(match[1]);
    const stored = sessionStorage.getItem('verix_csrf');
    if (cookieValue && cookieValue !== stored) {
      sessionStorage.setItem('verix_csrf', cookieValue);
    }
  }
}

/** Read CSRF token: sessionStorage first (cross-domain), then cookie fallback */
function getCsrfFromCookie(): string {
  if (typeof sessionStorage !== 'undefined') {
    const stored = sessionStorage.getItem('verix_csrf');
    if (stored) return stored;
  }
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/(?:^|;\s*)verix_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

async function refreshAccessToken(): Promise<{ csrfToken?: string } | null> {
  const res = await fetch(API_BASE + '/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const error: any = new Error('Refresh failed');
    // `REFRESH_RACE`: otra petición con esta misma cookie acaba de rotarla. El
    // backend no ha borrado nada y el llamador NO debe cerrar la sesión.
    const body = await res.json().catch(() => null);
    if (body?.code === 'REFRESH_RACE') error.code = 'REFRESH_RACE';
    throw error;
  }
  return res.json().catch(() => null);
}

/**
 * Ejecuta `fn` bajo el Web Lock `verix-refresh`, compartido por todas las
 * pestañas del origen. `waited` es true si otra pestaña lo tenía cogido: en ese
 * caso probablemente ya refrescó y `fn` debe comprobarlo antes de repetir.
 * Sin `navigator.locks` (navegadores antiguos, jsdom) se ejecuta tal cual y
 * queda solo el candado en memoria de esta pestaña, que es lo que había antes.
 */
async function withRefreshLock<T>(fn: (waited: boolean) => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return fn(false);
  // Primero sin esperar, para saber si hubo que hacer cola detrás de otra pestaña.
  const immediate = await locks.request(REFRESH_LOCK_NAME, { ifAvailable: true }, async (lock) =>
    lock ? { value: await fn(false) } : null,
  );
  if (immediate) return immediate.value;
  return locks.request(REFRESH_LOCK_NAME, () => fn(true));
}

/**
 * Resultado de `refreshSessionCoordinated`:
 * - `refreshed`: esta pestaña canjeó el refresh y hay cookies nuevas.
 * - `already-refreshed`: otra pestaña tenía el lock y, al comprobarlo, la
 *   sesión ya funcionaba; no se ha gastado el refresh token.
 * - `race`: el backend respondió REFRESH_RACE (otra petición con la misma
 *   cookie acaba de rotarla) y no ha borrado nada. Toca reintentar una vez;
 *   no es una sesión muerta.
 */
export type RefreshOutcome = 'refreshed' | 'already-refreshed' | 'race';

/**
 * Refresh coordinado entre pestañas: el ÚNICO camino para canjear el refresh
 * token (lo usan `apiFetch` ante un 401 y el arranque de `AuthContext`).
 * Si hubo que esperar el lock, llama antes a `stillUnauthorized`: con la
 * rotación de Cognito encendida, canjear dos veces el mismo token es justo la
 * carrera, así que primero se mira si la otra pestaña ya dejó cookies válidas.
 * Lanza si el refresh falla por otra causa (sesión muerta).
 */
export async function refreshSessionCoordinated(
  stillUnauthorized?: () => Promise<boolean>,
): Promise<RefreshOutcome> {
  return withRefreshLock(async (waited) => {
    if (waited && stillUnauthorized && !(await stillUnauthorized())) return 'already-refreshed';
    try {
      const refreshData = await refreshAccessToken();
      if (refreshData?.csrfToken) storeCsrfToken(refreshData.csrfToken);
      return 'refreshed';
    } catch (err: any) {
      if (err?.code === 'REFRESH_RACE') return 'race';
      throw err;
    }
  });
}

/**
 * Fetch with exponential backoff retry for transient errors.
 * Retries on network failures (TypeError) and HTTP 502/503/504.
 * Does NOT retry on 401 (handled separately), 400, 403, 404, etc.
 */
// Only retry idempotent methods — POST can cause duplicate mutations on 502 (gateway timeout but Lambda processed)
const IDEMPOTENT_METHODS = new Set(['GET', 'PUT', 'DELETE', 'HEAD', 'OPTIONS']);

async function _fetchWithRetry(url: string, fetchOpts: RequestInit, retry = true): Promise<Response> {
  const method = ((fetchOpts.method as string) || 'GET').toUpperCase();
  const canRetry = retry && IDEMPOTENT_METHODS.has(method);
  let attempt = 0;
  while (true) {
    let res: Response;
    try {
      res = await fetch(url, fetchOpts);
    } catch (err: any) {
      if (err.name === 'AbortError' || !canRetry || attempt >= MAX_RETRIES) throw err;
      attempt++;
      await _delay(RETRY_BASE_DELAY * Math.pow(2, attempt - 1));
      continue;
    }

    if (canRetry && RETRYABLE_STATUS_CODES.includes(res.status) && attempt < MAX_RETRIES) {
      attempt++;
      await _delay(RETRY_BASE_DELAY * Math.pow(2, attempt - 1));
      continue;
    }

    return res;
  }
}

function _delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Normaliza el body de error que devuelve el backend. Tres shapes posibles:
 *   - `{ message: 'string' }`            — older endpoints
 *   - `{ error: 'string' }`              — older endpoints
 *   - `{ error: { code, message?, details? } }`  — Zod / parseQuery shape (estándar
 *                                                    actual del API). Esta es la
 *                                                    que causaba "[object Object]"
 *                                                    cuando se pasaba a `new Error`
 *                                                    sin desempaquetar.
 *
 * Devuelve una versión normalizada con un `message` legible siempre, y hoistea
 * `code`/`details` del objeto anidado para que los callers puedan ramificar
 * sin recordar la profundidad.
 */
function extractErrorInfo(body: any, status: number): { message: string; code?: string; details?: any } {
  if (typeof body?.message === 'string') return { message: body.message, code: body.code, details: body.details };
  if (typeof body?.error === 'string') return { message: body.error, code: body.code, details: body.details };
  if (body?.error && typeof body.error === 'object') {
    const nested = body.error;
    const fallback = nested.code ? String(nested.code) : `API error ${status}`;
    return {
      message: typeof nested.message === 'string' ? nested.message : fallback,
      code: nested.code ?? body.code,
      details: nested.details ?? body.details,
    };
  }
  return { message: `API error ${status}` };
}

/**
 * Construye un Error decorado con status/details/code/etc. desde un body que
 * pueda tener varios shapes. Centraliza la lógica que antes estaba duplicada
 * en 3 sitios (línea 250 retry, 258 403, 272 default).
 */
function buildApiError(body: any, status: number): Error {
  const info = extractErrorInfo(body, status);

  // Dev-only logging: muestra fieldErrors y body completo para debugging.
  // En prod queda mudo para no llenar la consola del usuario.
  if (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.DEV) {
    // eslint-disable-next-line no-console
    console.error('[apiFetch] non-OK response', { status, body, normalized: info });
  }

  const error: any = new Error(info.message);
  error.status = status;
  if (info.code) error.code = info.code;
  if (info.details) error.details = info.details;
  // Preserve other top-level fields used downstream (toastApiError, 409 reactivate flow, etc.)
  if (body?.field) error.field = body.field;
  // Permiso exigido por el gate de autorización. Se preserva para diagnóstico —era el
  // único campo del contrato de error que se tiraba por el camino—, pero NO se enseña:
  // es un identificador de máquina. Lo fija un test en `resolveErrorMessage`.
  if (body?.required) error.required = body.required;
  // `reason` de EMISSION_REQUIRES_CERT: distingue las DOS acciones que el usuario tiene
  // que hacer —'no_cert' subir el certificado, 'expired' renovarlo— bajo un mismo code.
  // Sin esta linea se caia aqui en silencio, y `verifactuService.jsx` ya lo leia
  // (`reason: error.reason`) creyendo que llegaba: siempre valia `undefined`. Es el caso
  // exacto que describe el comentario de abajo sobre esta lista blanca.
  if (body?.reason) error.reason = body.reason;
  if (body?.existing_id) error.existing_id = body.existing_id;
  if (body?.existing_name) error.existing_name = body.existing_name;
  // #1149 — dedup de gasto: el 409 DUPLICATE_EXPENSE trae el gasto existente
  // (objeto) + el tipo de match para que el diálogo de confirmación los muestre.
  if (body?.existing && typeof body.existing === 'object') error.existing = body.existing;
  if (body?.match_type) error.match_type = body.match_type;
  // #2373 — el reparto cooperativo del 202 trae la BASE IMPONIBLE del período, y sin
  // ella la superficie donde se declara no puede pintar su contador de cuadre: esa cifra
  // solo la sabe el motor, cuyo cálculo se detiene justo en ese error.
  //
  // 🔴 Esta lista blanca es la razón de la regla «se ramifica por `code`, nunca por un
  // campo al lado» (verix-api#2047): lo que no está aquí se cae en el transporte SIN
  // avisar, y el consumidor lee `undefined` como si el backend no lo hubiera mandado.
  // Por eso el campo se añade aquí a la vez que se emite allí, no después.
  if (body?.base_imponible != null) error.base_imponible = body.base_imponible;
  if (body?.tasks_count != null) error.tasks_count = body.tasks_count;
  if (body?.status_name) error.status_name = body.status_name;
  // Preserve employees list (422 EMPLOYEES_WITHOUT_IBAN payroll SEPA export flow).
  if (Array.isArray(body?.employees)) error.employees = body.employees;
  // #1326 — 422 RECEIVED_INVOICE_COMPLIANCE_BLOCKED trae los códigos de bloqueo
  // (blockedBy) + issues para que la UI muestre el campo concreto que falla.
  if (Array.isArray(body?.blockedBy)) error.blockedBy = body.blockedBy;
  if (Array.isArray(body?.issues)) error.issues = body.issues;
  return error;
}

// Default request timeout (ms) â AI endpoints get longer
const DEFAULT_TIMEOUT = 30_000;
const AI_TIMEOUT = 35_000; // API Gateway timeout is 29s — add margin for network latency
const AI_PATHS = ['/api/functions/ai-', '/api/functions/invoke', '/api/v1/functions/ai-', '/api/v1/functions/invoke'];

export async function apiFetch<T = any>(path: string, options?: ApiFetchOptions): Promise<T> {
  const opts = options || {};
  const method = (opts.method || 'GET').toUpperCase();

  // Rate-limit /api/functions/invoke to prevent auto-invoke storms
  if (method === 'POST' && path === '/api/functions/invoke') {
    if (!checkInvokeRateLimit()) {
      return Promise.reject(new Error('Invoke rate limit exceeded â too many AI calls'));
    }
  }

  // Deduplicate identical in-flight GET/POST-invoke reads
  let dedupeKey: string | null = null;
  const isAuthPath = path.startsWith('/api/auth/');
  if (opts.dedupe !== false && !isAuthPath && (method === 'GET' || (method === 'POST' && path === '/api/functions/invoke' && opts.body))) {
    dedupeKey = `${method}:${path}:${opts.body || ''}`;
    const existing = inflightRequests.get(dedupeKey);
    if (existing) return existing;
  }

  const promise = _apiFetchInner<T>(path, opts);
  if (dedupeKey) {
    inflightRequests.set(dedupeKey, promise);
    // Use then(cb, cb) instead of finally(cb) — finally() creates a derived rejected
    // promise that is never caught, causing spurious "Uncaught (in promise)" in the console.
    promise.then(
      () => inflightRequests.delete(dedupeKey!),
      () => inflightRequests.delete(dedupeKey!),
    );
  }
  return promise;
}

async function _apiFetchInner<T>(path: string, options: ApiFetchOptions): Promise<T> {
  // Migrate /api/ to /api/v1/ for non-auth paths (backend /api/ is deprecated, sunset 2027-01-01)
  if (path.startsWith('/api/') && !path.startsWith('/api/v1/') && !path.startsWith('/api/auth/')) {
    path = path.replace('/api/', '/api/v1/');
  }
  const url = API_BASE + path;
  const csrfToken = getCsrfFromCookie();
  // Multipart uploads (body es FormData): el navegador necesita setear el
  // header `Content-Type: multipart/form-data; boundary=...` con un boundary
  // único auto-generado. Si forzamos `application/json` aquí, el boundary
  // nunca se setea y el endpoint multer rechaza el body. Detect + omit.
  // CSRF + auth header se mantienen normales (Express los lee del header).
  const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormDataBody ? {} : { 'Content-Type': 'application/json' }),
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    ...(options.headers || {}),
  };
  const timeoutMs = options.timeout || (AI_PATHS.some((p) => path.startsWith(p)) ? AI_TIMEOUT : DEFAULT_TIMEOUT);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  if (options.signal?.aborted) controller.abort();
  else options.signal?.addEventListener('abort', onExternalAbort, { once: true });
  const fetchOpts: RequestInit = {
    credentials: 'include',
    ...options,
    signal: controller.signal,
    headers,
  };
  delete (fetchOpts as any).timeout;
  delete (fetchOpts as any).dedupe;
  delete (fetchOpts as any).retry;
  delete (fetchOpts as any).responseType;

  let res: Response;
  try {
    res = await _fetchWithRetry(url, fetchOpts, options.retry !== false);
  } catch (err: any) {
    clearTimeout(timeoutId);
    options.signal?.removeEventListener('abort', onExternalAbort);
    if (options.signal?.aborted) throw err;
    if (err.name === 'AbortError') throw new Error(`Request timeout after ${timeoutMs / 1000}s: ${path}`);
    throw err;
  }
  clearTimeout(timeoutId);
  options.signal?.removeEventListener('abort', onExternalAbort);

  // Auto-refresh on 401 (refresh token is in httpOnly cookie)
  // Skip refresh for auth endpoints - a 401 there means bad credentials, not an expired token
  const isAuthPath = path.startsWith('/api/auth/');
  if (res.status === 401 && !isAuthPath) {
    // Se lee ANTES del candado de refresh: un cierre por inactividad no se
    // arregla refrescando, y no debe ni encolarse detrás de otro refresh.
    const unauthorizedBody = await Promise.resolve().then(() => res.json()).catch(() => null);
    if (unauthorizedBody?.code === SESSION_IDLE_TIMEOUT) {
      handleIdleTimeout();
      throw buildApiError(unauthorizedBody, 401);
    }
    if (isRefreshing) {
      const result = await new Promise<string | null>((resolve) => {
        refreshQueue.push(resolve);
      });
      if (!result) throw new Error('Refresh failed');
      // Retry with new cookie (automatically included)
      const retryHeaders = { ...headers, 'X-CSRF-Token': getCsrfFromCookie() };
      return fetch(url, { ...fetchOpts, headers: retryHeaders })
        .then(async (r) => {
          if (!r.ok && !(options.responseType === 'metadata' && r.status === 304)) throw buildApiError(await r.json().catch(() => ({})), r.status);
          return readApiResponse<T>(r, options);
        })
        .catch((e) => Promise.reject(e));
    }
    isRefreshing = true;
    // true cuando la sesión consta viva por otra vía (otra pestaña ya refrescó, o
    // el backend respondió REFRESH_RACE): si la petición falla después, se
    // propaga el error SIN logout. Una carrera no es una sesión muerta.
    let sessionAliveElsewhere = false;
    try {
      // Si otra pestaña tenía el lock, se repite la petición antes de gastar el
      // refresh token. Repetirla es seguro: un 401 sale de la autenticación,
      // antes de que el handler ejecute nada. Si ya pasa, esa respuesta ES el
      // reintento.
      let probe = null as Response | null;
      const outcome = await refreshSessionCoordinated(async () => {
        probe = await fetch(url, { ...fetchOpts, headers: { ...headers, 'X-CSRF-Token': getCsrfFromCookie() } });
        return probe.status === 401;
      });
      // `race`: el backend no ha borrado cookies; se reintenta una vez sin logout.
      sessionAliveElsewhere = outcome !== 'refreshed';
      isRefreshing = false;
      processQueue(true);
      // Retry — new access token cookie is set by server
      const retry: Response =
        outcome === 'already-refreshed' && probe
          ? probe
          : await fetch(url, { ...fetchOpts, headers: { ...headers, 'X-CSRF-Token': getCsrfFromCookie() } });
      if (!retry.ok && !(options.responseType === 'metadata' && retry.status === 304)) {
        const retryErr = await retry.json().catch(() => ({ message: retry.statusText }));
        throw buildApiError(retryErr, retry.status);
      }
      return readApiResponse<T>(retry, options);
    } catch (refreshErr) {
      isRefreshing = false;
      processQueue(false);
      if (sessionAliveElsewhere) throw refreshErr;
      // Clear cookies server-side and redirect
      await fetch(API_BASE + '/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
      // import.meta.env.BASE_URL (Vite, ya termina en "/") en vez de un
      // literal "/app/": sigue solo al `base` de vite.config.js si vuelve a
      // cambiar donde vive la SPA.
      rememberLoginReturn({ userId: storedUserId() });
      window.location.href = `${import.meta.env.BASE_URL}login`;
      throw refreshErr;
    }
  }

  // CSRF race condition recovery: if the backend rotated the token after a previous
  // mutation, sessionStorage may carry the old value. Re-sync from cookie and retry once.
  // Safe for POST because CSRF validation runs before the handler — no mutation has fired yet.
  if (res.status === 403) {
    const errBody = await res.json().catch(() => ({ error: '' }));
    if (errBody.error === 'Invalid CSRF token') {
      syncCsrfFromCookie();
      const retryHeaders = { ...headers, 'X-CSRF-Token': getCsrfFromCookie() };
      const retryRes = await _fetchWithRetry(url, { ...fetchOpts, headers: retryHeaders }, options.retry !== false);
      if (!retryRes.ok) {
        const retryErr = await retryRes.json().catch(() => ({ message: retryRes.statusText }));
        throw buildApiError(retryErr, retryRes.status);
      }
      return readApiResponse<T>(retryRes, options);
    }
    // Non-CSRF 403: build error from already-read body (can't call res.json() twice)
    throw buildApiError(errBody, 403);
  }

  if (options.responseType === 'metadata' && res.status === 304) return readApiResponse<T>(res, options);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw buildApiError(err, res.status);
  }

  // After successful mutations, the backend may rotate the CSRF token via Set-Cookie.
  // Sync the cookie value to sessionStorage so the next request uses the fresh token.
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes((options.method || 'GET').toUpperCase())) {
    syncCsrfFromCookie();
  }

  return readApiResponse<T>(res, options);
}

async function readApiResponse<T>(res: Response, options: ApiFetchOptions): Promise<T> {
  if (options.responseType === 'metadata') {
    const data = res.status === 304 || res.status === 204 || res.headers?.get?.('content-length') === '0' ? null : await res.json();
    return { status: res.status, etag: res.headers?.get?.('etag') ?? null, data } as T;
  }
  if (res.status === 204 || res.headers?.get?.('content-length') === '0') return null as T;
  // `blob` para los documentos del servidor (el PDF de la factura). Va DESPUÉS
  // de todo lo demás a propósito: los errores del servidor siguen llegando en
  // JSON aunque lo que se pida sea un PDF, así que el parseo de error de arriba
  // no puede depender de esto.
  if (options.responseType === 'blob') return (await res.blob()) as T;
  return res.json();
}

/**
 * Build the standard auth headers for API requests.
 * Useful for SSE/streaming calls that cannot use apiFetch directly.
 */
export function getApiHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfFromCookie(),
  };
}

export default apiFetch;

export function unwrapData<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  if (response && typeof response === 'object' && Array.isArray((response as any).data)) {
    return (response as any).data as T[];
  }
  return [];
}

export function unwrapOne<T>(response: unknown): T | null {
  if (response && typeof response === 'object') {
    if ('data' in (response as any)) return (response as any).data as T;
  }
  return response as T | null;
}

export function unwrapTotal(response: unknown): number {
  if (response && typeof response === 'object' && 'total' in (response as any)) {
    return (response as any).total as number;
  }
  if (Array.isArray(response)) return response.length;
  return 0;
}

/**
 * unwrapPoints — extrae el array `points` de respuestas de series temporales.
 * Algunos endpoints (cashflow, kpi-sparkline) devuelven `{ points: [...] }`
 * en vez del envelope `{ data: [...] }` estándar. Este helper centraliza la
 * extracción y degrada a `[]` ante cualquier shape inesperado.
 */
export function unwrapPoints<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  if (response && typeof response === 'object' && Array.isArray((response as any).points)) {
    return (response as any).points as T[];
  }
  return [];
}
